import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  ButtonV2,
  NestedDropdown,
  TableComponentV2,
  LabraToolTipV2,
  CheckBoxFieldv2,
  TextInputV2,
  ModalConfirmV2,
  ModalSizeVariants,
  DatePickerV2,
  labraUIDateFormat,
} from '@labrav/react-components'
import { useStyles } from './MarketplaceTemplateTableContainer.styles'
import {
  faInfoCircle,
  faUpload,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import {
  buildHubspotCrmFieldMenuEntries,
  commonCosellMappingMenuData,
  getInputType,
  hubspotFieldInputTypes,
  RenderNestedSelectProps,
  withSortedNoneCrmMenuOption,
} from '../../utils/mappingManagerConstants'
import { useDispatch, useSelector } from '../../../../store'
import {
  MarketplaceFieldMapping,
  MarketplaceHubspotFieldsOptions,
  MarketplaceTemplatesDataFieldMappings,
} from '../../../../modules/marketplaceMappingManager/types'
import { updateMarketplaceTemplateSectionField } from '../../../../modules/marketplaceMappingManager/action'
import { isEmpty } from 'lodash'
import {
  FileUploadWithLoader,
  getFileLinksFromUrl,
  defaultNow,
  bucketName,
  useAccountId,
  LoadingTypes,
  isLoading as Loading,
} from '@labrav/common'
import { ShortenedName } from '../../../../common/components/ShortenedName/ShortenedName'
import { Chip, TableCell, Typography } from '@mui/material'
import { MapCrmFields } from '../../MapCrmFields/MapCrmFields'
import { useLocation } from 'react-router-dom'
import {
  eulaFileUploadButtonText,
  eulaFileUploadContainerTitle,
  fileFormatIncorrectText,
  mappingManagerEulaFileUploadDescription,
} from '../../../../common/utils/productCreationUtil'

interface CustomHeaderCellProps {
  label: string
  tooltip?: string
  className?: string
}

const CustomHeaderCell = React.memo(
  ({ label, tooltip, className }: CustomHeaderCellProps) => {
    const { classes } = useStyles()
    return (
      <TableCell className={className || classes.tableCell}>
        {tooltip ? (
          <div className={classes.headerWithInfo}>
            <Typography className={classes.header}>{label}</Typography>
            <LabraToolTipV2
              tooltipPlacement="top-start"
              description={tooltip}
              tooltipPlacmentGap="12px"
            >
              <FontAwesomeIcon
                icon={faInfoCircle}
                className={classes.headerInfoIcon}
              />
            </LabraToolTipV2>
          </div>
        ) : (
          <Typography className={classes.header}>{label}</Typography>
        )}
      </TableCell>
    )
  }
)

const MarketplaceNestedSelect = React.memo(
  ({
    mapButton = false,
    mapButtonDisabled = false,
    disabled = false,
    menuData,
    onSelectionChange,
    defaultSelectedItems,
    hubspotField,
    hubspotFieldData,
    fieldKey,
    picklistMappings,
    picklist,
    hubspotObject,
    isViewOnly,
    width = '420px',
  }: RenderNestedSelectProps & { width?: string }) => {
    const { classes } = useStyles()
    const dispatch = useDispatch()
    const [openModal, setOpenModal] = useState(false)
    const inputSelectRef = useRef<HTMLDivElement>(null)
    const [inputSelectWidth, setInputSelectWidth] = useState<number>()

    useLayoutEffect(() => {
      const inputSelect = inputSelectRef.current
      if (!inputSelect || width !== '100%') return

      const updateWidth = () => {
        setInputSelectWidth(inputSelect.getBoundingClientRect().width)
      }
      const resizeObserver = new ResizeObserver(updateWidth)
      resizeObserver.observe(inputSelect)
      updateWidth()

      return () => resizeObserver.disconnect()
    }, [width])

    const handleButtonClick = useCallback(() => setOpenModal(true), [])

    const handleSelectionChange = useCallback(
      (
        selectedValues: string[],
        selectedPaths: string[],
        hubspotObject?: string,
        isMapped?: boolean
      ) => {
        if (selectedValues[0] === '') {
          onSelectionChange?.([''], [''], '', false)
          return
        }

        if (!selectedValues[0] || !selectedPaths[0]) return

        const pathParts = selectedPaths[0].split('.')
        if (pathParts.length < 2) return

        const hubspotObj = pathParts[0]
        const hubspotFieldLabel = pathParts.slice(1).join('.')
        const hubspotFieldValue = selectedValues[0]

        if (onSelectionChange) {
          onSelectionChange(
            [hubspotFieldValue],
            [hubspotFieldLabel],
            hubspotObj,
            true
          )
        }
      },
      [onSelectionChange]
    )

    const updateAction = useCallback(
      (_: any, fieldKey: any, data: { picklistMappings: any[] }) => {
        dispatch(
          updateMarketplaceTemplateSectionField(fieldKey, {
            picklistMappings: data.picklistMappings,
          })
        )
      },
      [dispatch]
    )

    return (
      <div className={classes.hubspotFields}>
        <div className={classes.inputSelect} ref={inputSelectRef}>
          <NestedDropdown
            menuData={menuData}
            placeholder="Select an option"
            width={
              width === '100%' && inputSelectWidth
                ? `${inputSelectWidth}px`
                : width
            }
            height="auto"
            allowMultiple={false}
            onSelectionChange={handleSelectionChange}
            defaultSelectedItems={defaultSelectedItems}
            disabled={disabled || isViewOnly}
          />
        </div>
        {mapButton && (
          <>
            <ButtonV2
              styleType="outlined"
              smallVariant
              disabled={mapButtonDisabled || isViewOnly}
              onClick={handleButtonClick}
              className={classes.mapButton}
            >
              Map
            </ButtonV2>
            <MapCrmFields
              hubspotField={hubspotField!}
              hubspotObject={hubspotObject!}
              hubspotFieldData={hubspotFieldData!}
              fieldKey={fieldKey!}
              picklistMappings={picklistMappings!}
              picklist={picklist!}
              category={undefined}
              openModal={openModal}
              setOpenModal={setOpenModal}
              context="marketplace"
              updateAction={updateAction}
            />
          </>
        )}
      </div>
    )
  }
)

const MarketplaceDefaultValueNestedSelect = React.memo(
  ({
    disabled = false,
    menuData,
    onSelectionChange,
    defaultSelectedItems,
    referralFieldType,
    width = '267px',
  }: RenderNestedSelectProps & {
    referralFieldType: string
    width?: string
  }) => {
    const { classes } = useStyles()

    const menuDataWithNone = React.useMemo(
      () => withSortedNoneCrmMenuOption(menuData),
      [menuData]
    )

    const handleSelectionChange = useCallback(
      (
        selectedValues: string[],
        selectedPaths: string[],
        hubspotObject?: string,
        isMapped?: boolean
      ) => {
        if (selectedValues[0] === '') {
          onSelectionChange?.([''], [''], '', false)
          return
        }

        if (!selectedValues[0] || !selectedPaths[0]) return

        onSelectionChange?.(selectedValues, selectedPaths, '', true)
      },
      [onSelectionChange]
    )

    return (
      <div className={classes.hubspotFields}>
        <div className={classes.inputSelect}>
          <NestedDropdown
            menuData={menuDataWithNone}
            placeholder="Select an option"
            width={width}
            height="44px"
            allowMultiple={referralFieldType === 'Multipicklist'}
            onSelectionChange={handleSelectionChange}
            defaultSelectedItems={defaultSelectedItems}
            disabled={disabled}
          />
        </div>
      </div>
    )
  }
)

const customerDetailsMapping = {
  'Fields to map': 'fieldsToMap',
  'Field type': 'fieldType',
  'Hubspot fields': 'hubspotFields',
  'Default value': 'defaultValue',
  Hidden: 'hidden',
  'Read only': 'readOnly',
}

export const matchesMarketplaceMappingField = (
  field: Pick<
    MarketplaceFieldMapping,
    'category' | 'label' | 'hubspotField' | 'defaultValue'
  >,
  searchTerm: string,
  section?: 'fields' | 'metadata',
  fieldFilter: 'all' | 'mapped' | 'unmapped' = 'all'
) => {
  const isMetadata = field.category?.toLowerCase() === 'metadata'
  const hasDefaultValue = Array.isArray(field.defaultValue)
    ? field.defaultValue.some(value => value.trim() !== '')
    : String(field.defaultValue ?? '').trim() !== ''
  const isMapped = !!field.hubspotField || hasDefaultValue

  return (
    (!section || (section === 'metadata' ? isMetadata : !isMetadata)) &&
    (field.label || '')
      .toLowerCase()
      .includes(searchTerm.trim().toLowerCase()) &&
    (fieldFilter === 'all' || (fieldFilter === 'mapped' ? isMapped : !isMapped))
  )
}

export const getMarketplaceDefaultValueInputType = ({
  variant,
  fieldKey,
  referralFieldType,
}: {
  variant: 'default' | 'azure'
  fieldKey: string
  referralFieldType: string
}) =>
  variant === 'azure' && fieldKey === 'Labra__MSFT_Billing_Account_ID__c'
    ? 'text'
    : getInputType(referralFieldType)

const MarketplaceMappingManagerTextInput = React.memo(
  ({
    keyName,
    type,
    value,
    onChange,
    disabled = false,
    width,
  }: {
    keyName: string
    type: string
    value: string
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    disabled?: boolean
    width?: string
  }) => {
    const { classes } = useStyles()
    return (
      <div className={classes.inputText} style={width ? { width } : undefined}>
        <TextInputV2
          keyName={keyName}
          title="Enter default value"
          placeHolder=""
          type={type}
          value={value}
          updatedValue={value}
          isError={false}
          onChange={onChange}
          isReadOnly={false}
          isRequired={false}
          errorText=""
          showSpinner={false}
          size="small"
          viewOnly={disabled}
        />
      </div>
    )
  }
)

const MarketplaceMappingManagerCheckbox = React.memo(
  ({
    keyName,
    value,
    onChange,
    checkboxDisabled,
  }: {
    keyName: string
    value: boolean
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    checkboxDisabled?: boolean
  }) => {
    const { classes } = useStyles()
    return (
      <div className={classes.checkBox}>
        <CheckBoxFieldv2
          key={keyName}
          keyName={keyName}
          title=""
          value={value}
          onChangeBox={onChange}
          disabled={checkboxDisabled}
        />
      </div>
    )
  }
)

const AZURE_FIELD_CELL_PROPS = [
  { style: { width: '20%' } },
  { style: { width: '10%' } },
  { style: { width: '30%' } },
  { style: { width: '30%' } },
  { style: { width: '5%' } },
  { style: { width: '5%' } },
]

const MarketplaceMappingManagerFileUpload = React.memo(
  ({
    fieldKey,
    disabled,
    width,
  }: {
    fieldKey: keyof MarketplaceTemplatesDataFieldMappings
    disabled: boolean
    width?: string
  }) => {
    const { classes } = useStyles()
    const dispatch = useDispatch()
    const accountId = useAccountId({ product: 'flyout' }) || ''

    const isFileUploading = useSelector<boolean>(state =>
      Loading(state.loading)(LoadingTypes.FILE_PROCESSING_LOADER)
    )

    const marketplaceTableDetails =
      useSelector(
        state =>
          state.marketplaceMappingManager?.templateData?.templateData
            ?.fieldMappings
      ) || []

    const [openModal, setOpenModal] = useState(false)
    const [eulaErr, setEulaErr] = useState('')
    const [tempUploadedFiles, setTempUploadedFiles] = useState<string[]>([])
    const [showAll, setShowAll] = useState(false)

    const getDefaultFileUrls = useCallback(() => {
      const defaultValue = (
        marketplaceTableDetails?.[fieldKey] as MarketplaceFieldMapping
      )?.defaultValue
      return Array.isArray(defaultValue)
        ? (defaultValue as string[])
        : defaultValue
          ? ([defaultValue] as string[])
          : []
    }, [fieldKey, marketplaceTableDetails])

    useEffect(() => {
      setTempUploadedFiles(getDefaultFileUrls())
    }, [getDefaultFileUrls])

    const handleOpenFileUploadModal = useCallback(() => {
      setTempUploadedFiles(getDefaultFileUrls())
      setOpenModal(true)
    }, [getDefaultFileUrls])

    const handleCloseModal = useCallback(() => {
      setTempUploadedFiles(getDefaultFileUrls())
      setOpenModal(false)
      setEulaErr('')
    }, [getDefaultFileUrls])

    const updateEulaUrl = useCallback((fileUrl: string, index = 0) => {
      setTempUploadedFiles(prev => {
        const updated = [...prev]
        if (index < updated.length) updated[index] = fileUrl
        else updated.push(fileUrl)
        return updated
      })
    }, [])

    const deleteEulaUrl = useCallback((index: number) => {
      setTempUploadedFiles(prev => {
        const updated = [...prev]
        updated.splice(index, 1)
        return updated
      })
    }, [])

    const updatedExistingFileLinks = useMemo(() => {
      return getFileLinksFromUrl(getDefaultFileUrls(), true)
    }, [getDefaultFileUrls])

    const uploadedFileNames = useMemo(
      () => updatedExistingFileLinks.map(file => file.name),
      [updatedExistingFileLinks]
    )

    const handleUploadEULAFiles = useCallback(() => {
      dispatch(
        updateMarketplaceTemplateSectionField(fieldKey, {
          defaultValue: tempUploadedFiles,
          isMapped: tempUploadedFiles.length > 0,
        })
      )
      setOpenModal(false)
    }, [dispatch, fieldKey, tempUploadedFiles])

    const handleShowAllClick = useCallback(() => setShowAll(true), [])

    const onDelete = useCallback(
      (fileNameToDelete: string) => {
        const updated = getDefaultFileUrls().filter(file => {
          const { name } = getFileLinksFromUrl([file], true)[0] || {}
          return name !== fileNameToDelete
        })

        dispatch(
          updateMarketplaceTemplateSectionField(fieldKey, {
            defaultValue: updated,
            isMapped: updated.length > 0,
          })
        )
      },
      [dispatch, fieldKey, getDefaultFileUrls]
    )

    const displayCount = 1
    const remainingCount = uploadedFileNames.length - displayCount
    const displayedValues = showAll
      ? uploadedFileNames
      : uploadedFileNames.slice(0, displayCount)

    const hasFiles = getDefaultFileUrls().length > 0

    return (
      <>
        {!hasFiles ? (
          <ButtonV2
            onClick={handleOpenFileUploadModal}
            disabled={disabled}
            className={classes.fileUploadBtn}
            style={width ? { width } : undefined}
            styleType="outlined"
            smallVariant
          >
            Upload files
          </ButtonV2>
        ) : (
          <div
            className={classes.selectedFileChipContainer}
            style={width ? { width } : undefined}
          >
            <div className={classes.selectedFileChipInnerContainer}>
              <div className={classes.chipContainer}>
                {displayedValues.map(option => (
                  <Chip
                    key={option}
                    variant="outlined"
                    className={classes.chip}
                    label={
                      <ShortenedName
                        name={option}
                        length={12}
                        tooltipPlacement="top"
                        isV2
                      />
                    }
                    onDelete={() => onDelete(option)}
                    deleteIcon={
                      <FontAwesomeIcon
                        icon={faXmark}
                        width="18px"
                        height="18px"
                      />
                    }
                  />
                ))}
                {remainingCount > 0 && !showAll && (
                  <Chip
                    label={`+${remainingCount}`}
                    className={classes.chip}
                    onClick={handleShowAllClick}
                  />
                )}
              </div>
            </div>
            <LabraToolTipV2 tooltipPlacement="top" description="Upload files">
              <ButtonV2
                onClick={handleOpenFileUploadModal}
                disabled={disabled}
                className={classes.fileUploadIcon}
                styleType="outlined"
                smallVariant
              >
                <FontAwesomeIcon icon={faUpload} />
              </ButtonV2>
            </LabraToolTipV2>
          </div>
        )}

        <ModalConfirmV2
          title="Upload EULA"
          icon={<FontAwesomeIcon icon={faUpload} />}
          onlyCloseModal
          onCloseModal={handleCloseModal}
          onDecline={handleCloseModal}
          open={openModal}
          onAccept={handleUploadEULAFiles}
          acceptDisabled={isFileUploading || tempUploadedFiles.length === 0}
          titleCancelButton="Cancel"
          titleAcceptButton="Submit"
          variant={ModalSizeVariants.large}
          hideCancelButton={false}
          content={
            <div className={classes.fileUploadContainer}>
              <FileUploadWithLoader
                keyName="custom-eula-file-upload"
                partnerId={accountId}
                existingFileLinks={tempUploadedFiles}
                assetType="eula_files"
                folder="product_eula_files"
                fileUploadDescription={mappingManagerEulaFileUploadDescription}
                quantity={5}
                fileUploadType="txt/pdf"
                unit="MB"
                uploadFileSize={3}
                acceptedFormats={['.pdf', '.txt']}
                updateUrl={updateEulaUrl}
                deleteUrl={deleteEulaUrl}
                defaultNow={defaultNow}
                fileUploadButtonText={eulaFileUploadButtonText}
                fileFormatIncorrectText={fileFormatIncorrectText}
                fileUploadContainerTitle={eulaFileUploadContainerTitle}
                bucketName={bucketName}
                isMultiFileUpload
                updateErr={setEulaErr}
                uploadError={eulaErr}
                viewOnly={false}
                checkDuplicate
                showRealFileName
              />
            </div>
          }
        />
      </>
    )
  }
)

export const MarketplaceTemplateTableContainer = React.memo(
  ({
    searchTerm = '',
    section,
    fieldFilter = 'all',
    variant = 'default',
    tableHeight,
  }: {
    searchTerm?: string
    section?: 'fields' | 'metadata'
    fieldFilter?: 'all' | 'mapped' | 'unmapped'
    variant?: 'default' | 'azure'
    tableHeight?: string
  }) => {
    const { classes } = useStyles()
    const dispatch = useDispatch()
    const location = useLocation()
    const queryParams = new URLSearchParams(location.search)
    const isViewOnly = queryParams.get('viewOnly') === 'true'
    const marketplaceTableDetails =
      useSelector(
        state =>
          state.marketplaceMappingManager?.templateData?.templateData
            ?.fieldMappings
      ) || []
    const hubspotFieldsData = useSelector(
      state => state.marketplaceMappingManager.hubspotFields.fields || []
    )

    const getMatchingHubspotFields = useCallback(
      (objectType: string, fieldType: string) => {
        return hubspotFieldsData.filter(
          field =>
            field.objectType === objectType &&
            field.fieldType === fieldType &&
            ((fieldType !== 'Picklist' && fieldType !== 'Multipicklist') ||
              (field.options && field.options.length > 0))
        )
      },
      [hubspotFieldsData]
    )

    const sortedMarketplaceTableDetails = useMemo(() => {
      if (!marketplaceTableDetails) return []
      return Object.entries(marketplaceTableDetails)
        .filter(([, value]) => value && typeof value.referralField === 'string')
        .map(([key, value]) => ({ key, ...value }))
        .filter(item =>
          matchesMarketplaceMappingField(item, searchTerm, section, fieldFilter)
        )
        .sort((a, b) => a.displayOrder - b.displayOrder)
    }, [marketplaceTableDetails, searchTerm, section, fieldFilter])

    const handleCheckboxChange = useCallback(
      (
        fieldKey: keyof MarketplaceTemplatesDataFieldMappings,
        prop: 'isHidden' | 'isReadonly'
      ) =>
        (e: React.ChangeEvent<HTMLInputElement>) => {
          dispatch(
            updateMarketplaceTemplateSectionField(fieldKey, {
              [prop]: e.target.checked,
            })
          )
        },
      [dispatch]
    )

    const handleDefaultValueInputChange = useCallback(
      (
        fieldKey: keyof MarketplaceTemplatesDataFieldMappings,
        e: React.ChangeEvent<HTMLInputElement>
      ) => {
        dispatch(
          updateMarketplaceTemplateSectionField(fieldKey, {
            defaultValue: e.target.value,
            isMapped: !!e.target.value,
          })
        )
      },
      [dispatch]
    )

    const handleDefaultDateChange = useCallback(
      (
        fieldKey: keyof MarketplaceTemplatesDataFieldMappings,
        date: string | null
      ) => {
        dispatch(
          updateMarketplaceTemplateSectionField(fieldKey, {
            defaultValue: date || '',
            isMapped: !!date,
          })
        )
      },
      [dispatch]
    )

    const handleHubspotFieldsSelectionChange = useCallback(
      (
        fieldKey: keyof MarketplaceTemplatesDataFieldMappings,
        referralFieldType: string,
        selectedValues: string[],
        selectedPaths: string[],
        hubspotObject?: string,
        isMapped: boolean = true
      ) => {
        dispatch(
          updateMarketplaceTemplateSectionField(fieldKey, {
            hubspotObject: hubspotObject || '',
            hubspotField: selectedValues[0] || '',
            hubspotFieldLabel: selectedPaths[0] || '',
            hubspotFieldType: selectedValues[0] ? referralFieldType : '',
            isMapped: isMapped,
          })
        )
      },
      [dispatch]
    )

    const handleDefaultValueSelectionChange = useCallback(
      (
        fieldKey: keyof MarketplaceTemplatesDataFieldMappings,
        selectedValues: string[],
        selectedPaths: string[],
        referralField: string,
        hubspotObject?: string,
        isMapped?: boolean
      ) => {
        if (selectedValues[0] === '') {
          dispatch(
            updateMarketplaceTemplateSectionField(fieldKey, {
              defaultValue: referralField === 'Multipicklist' ? [] : '',
              isMapped: false,
            })
          )
          return
        }

        dispatch(
          updateMarketplaceTemplateSectionField(fieldKey, {
            defaultValue:
              referralField === 'Multipicklist'
                ? selectedValues
                : selectedValues[0],
            isMapped: isMapped !== false,
          })
        )
      },
      [dispatch]
    )

    const marketplaceTableData = useMemo(() => {
      return sortedMarketplaceTableDetails.map(item => {
        const isPicklist = item.referralFieldType === 'Picklist'
        const isFile = item.referralFieldType === 'File'
        const isMultiPicklist = item.referralFieldType === 'Multipicklist'
        const fieldKey = item.key as keyof MarketplaceTemplatesDataFieldMappings

        const menuDataForRow = [
          { ...commonCosellMappingMenuData[0] },
          ...buildHubspotCrmFieldMenuEntries({
            fieldType: item.referralFieldType,
            getMatchingFields: getMatchingHubspotFields,
          }),
        ]

        return {
          id: item.referralField,
          fieldsToMap: (
            <div className={classes.fieldsToMapRow}>
              <Typography className={classes.fieldsToMap}>
                {item.label}
              </Typography>
            </div>
          ),
          fieldType: (
            <div className={classes.fieldsToMapRow}>
              <Typography className={classes.fieldsToMap}>
                {isMultiPicklist
                  ? 'Multi Picklist'
                  : isFile
                    ? variant === 'azure'
                      ? '.txt/.pdf'
                      : 'PDF/TXT'
                    : item.referralFieldType}
              </Typography>
            </div>
          ),
          hubspotFields: (
            <MarketplaceNestedSelect
              menuData={menuDataForRow}
              onSelectionChange={(
                selectedValues: string[],
                selectedPaths: string[],
                hubspotObject?: string,
                isMapped?: boolean
              ) =>
                handleHubspotFieldsSelectionChange(
                  fieldKey,
                  item.referralFieldType,
                  selectedValues,
                  selectedPaths,
                  hubspotObject,
                  isMapped
                )
              }
              {...(item.hubspotObject && item.hubspotFieldLabel
                ? {
                    defaultSelectedItems: [
                      {
                        value: `${item.hubspotObject}.${item.hubspotField}`,
                        path: `${item.hubspotObject}.${item.hubspotFieldLabel}`,
                      },
                    ],
                  }
                : {})}
              disabled={!isEmpty(item.defaultValue)}
              isViewOnly={isViewOnly}
              width={variant === 'azure' ? '100%' : '420px'}
              {...(variant !== 'azure' && (isPicklist || isMultiPicklist)
                ? { mapButton: true }
                : {})}
              {...(variant !== 'azure' && (isPicklist || isMultiPicklist)
                ? {
                    mapButtonDisabled:
                      isEmpty(item.hubspotField) || !isEmpty(item.defaultValue),
                  }
                : {})}
              hubspotField={item.hubspotField}
              hubspotFieldData={hubspotFieldsData}
              hubspotObject={item.hubspotObject}
              fieldKey={fieldKey}
              picklistMappings={item.picklistMappings || []}
              picklist={item.picklist || []}
            />
          ),
          defaultValue:
            isPicklist || isMultiPicklist ? (
              <MarketplaceDefaultValueNestedSelect
                menuData={(item.picklist || []).map(
                  (option: MarketplaceHubspotFieldsOptions) => ({
                    ...option,
                    options: [],
                  })
                )}
                disabled={!isEmpty(item.hubspotObject) || isViewOnly}
                {...(item.defaultValue
                  ? {
                      defaultSelectedItems: Array.isArray(item.defaultValue)
                        ? item.defaultValue.map((value: string) => ({
                            value,
                            path:
                              item.picklist?.find(
                                (p: MarketplaceHubspotFieldsOptions) =>
                                  p.value === value
                              )?.label || value,
                          }))
                        : [
                            {
                              value: item.defaultValue,
                              path:
                                item.picklist?.find(
                                  (p: MarketplaceHubspotFieldsOptions) =>
                                    p.value === item.defaultValue
                                )?.label || item.defaultValue,
                            },
                          ],
                    }
                  : {})}
                onSelectionChange={(
                  selectedValues: string[],
                  selectedPaths: string[],
                  hubspotObject?: string,
                  isMapped?: boolean
                ) =>
                  handleDefaultValueSelectionChange(
                    fieldKey,
                    selectedValues,
                    selectedPaths,
                    item.referralFieldType,
                    hubspotObject,
                    isMapped
                  )
                }
                referralFieldType={item.referralFieldType}
                width={variant === 'azure' ? '100%' : '267px'}
              />
            ) : isFile ? (
              <MarketplaceMappingManagerFileUpload
                fieldKey={fieldKey}
                width={variant === 'azure' ? '100%' : undefined}
                disabled={
                  (!!item.hubspotObject && !!item.hubspotField) || isViewOnly
                }
              />
            ) : variant === 'azure' && item.referralFieldType === 'Date' ? (
              <div className={classes.inputText} style={{ width: '100%' }}>
                <DatePickerV2
                  format={labraUIDateFormat}
                  views={['year', 'month', 'day']}
                  field={{
                    title: '',
                    humanReadableKey: 'Select a date',
                    placeHolder: 'Select a date',
                    id: `azure-marketplace-default-${fieldKey}`,
                    isRequired: false,
                    isReadOnly: !!item.hubspotField || isViewOnly,
                    isCustom: false,
                    defaultValue: item.defaultValue || '',
                    dataType: 'date',
                  }}
                  handleDateChange={date =>
                    handleDefaultDateChange(fieldKey, date)
                  }
                  size="small"
                />
              </div>
            ) : hubspotFieldInputTypes.includes(item.referralFieldType) ? (
              <MarketplaceMappingManagerTextInput
                keyName={`${item.referralField}-defaultValue`}
                type={getMarketplaceDefaultValueInputType({
                  variant,
                  fieldKey,
                  referralFieldType: item.referralFieldType,
                })}
                value={
                  Array.isArray(item.defaultValue)
                    ? item.defaultValue[0] || ''
                    : item.defaultValue
                }
                onChange={e => handleDefaultValueInputChange(fieldKey, e)}
                width={variant === 'azure' ? '100%' : undefined}
                disabled={
                  (!!item.hubspotObject && !!item.hubspotField) || isViewOnly
                }
              />
            ) : null,
          hidden: (
            <MarketplaceMappingManagerCheckbox
              keyName={`${item.referralField}-hidden`}
              value={item.isHidden}
              onChange={handleCheckboxChange(fieldKey, 'isHidden')}
              checkboxDisabled={
                (variant !== 'azure' && item.isReadonly) || isViewOnly
              }
            />
          ),
          readOnly: (
            <MarketplaceMappingManagerCheckbox
              keyName={`${item.referralField}-readOnly`}
              value={item.isReadonly}
              onChange={handleCheckboxChange(fieldKey, 'isReadonly')}
              checkboxDisabled={
                (variant !== 'azure' && item.isHidden) || isViewOnly
              }
            />
          ),
        }
      })
    }, [
      sortedMarketplaceTableDetails,
      classes,
      handleHubspotFieldsSelectionChange,
      handleDefaultValueSelectionChange,
      handleDefaultValueInputChange,
      handleCheckboxChange,
      hubspotFieldsData,
      getMatchingHubspotFields,
      handleDefaultDateChange,
      isViewOnly,
      variant,
    ])

    const customHeaders = useMemo(
      () => ({
        fieldsToMap: (
          <CustomHeaderCell
            label="Fields to map"
            className={clsx(classes.tableCell, classes.fieldsToMapCell)}
          />
        ),
        fieldType: (
          <CustomHeaderCell
            label="Field type"
            className={clsx(classes.tableCell, classes.fieldTypeCell)}
          />
        ),
        hubspotFields: (
          <CustomHeaderCell
            label="HubSpot fields"
            className={clsx(
              classes.tableCell,
              variant === 'azure'
                ? classes.azureEqualColumn
                : classes.hubspotFieldsTableCell
            )}
          />
        ),
        defaultValue: (
          <CustomHeaderCell
            label="Default value"
            className={clsx(
              classes.tableCell,
              variant === 'azure'
                ? classes.azureEqualColumn
                : classes.defaultValueCell
            )}
          />
        ),
        hidden: (
          <CustomHeaderCell
            label="Hidden"
            tooltip="Hides the inputs from Labra offers creation page. If this is checked, then the 'read only' checkbox will be disabled."
            className={clsx(classes.tableCell, classes.customCell)}
          />
        ),
        readOnly: (
          <CustomHeaderCell
            label="Read only"
            tooltip="Sets the inputs as 'read only for the Labra offers creation page"
            className={clsx(classes.tableCell, classes.customCell)}
          />
        ),
      }),
      [classes, variant]
    )

    return (
      <div
        data-testid="marketplace-template-form-table"
        className={classes.tableContainer}
      >
        {marketplaceTableData.length === 0 ? (
          <Typography>
            {searchTerm || fieldFilter !== 'all'
              ? 'No matching fields'
              : 'No fields available'}
          </Typography>
        ) : (
          <TableComponentV2
            keyName="marketplace-template-form-table"
            objects={marketplaceTableData}
            customHeader={customHeaders}
            mapping={customerDetailsMapping}
            fetchMoreData={() => {}}
            selectedItem={[]}
            onChangeCheckbox={() => {}}
            showCheckbox={false}
            breakWorkHeader={false}
            tableHeight={tableHeight ?? 'calc(100vh - 250px)'}
            hasMoreData={false}
            customCellProps={
              variant === 'azure' ? AZURE_FIELD_CELL_PROPS : undefined
            }
            columnWidths={
              variant === 'azure'
                ? {
                    fieldsToMap: '20%',
                    fieldType: '10%',
                    hubspotFields: '30%',
                    defaultValue: '30%',
                    hidden: '5%',
                    readOnly: '5%',
                  }
                : undefined
            }
          />
        )}
      </div>
    )
  }
)
